import React, { useState } from "react";
import { Waypoint } from "react-waypoint";

import CovidHerdImmunityController from "./CovidHerdImmunityController";
import plotImg from "./plot.svg";

function CovidHerdImmunity() {
  const [startFirstSimulation, setStartFirstSimulation] = useState(false);
  const [startSecondSimulation, setStartSecondSimulation] = useState(false);
  const [startThirdSimulation, setStartThirdSimulation] = useState(false);
  const [startFourthSimulation, setStartFourthSimulation] = useState(false);
  const [startFifthSimulation, setStartFifthSimulation] = useState(false);
  const [startCustomSimulation, setStartCustomSimulation] = useState(false);

  return (
    <>
      <p>
        Face à la crise du coronavirus, le gouvernement va-t-il se résoudre à
        sacrifier des dizaines, voire des centaines de milliers de Français ?
        L’hypothèse n’est pas aussi insensée que l’on pourrait le croire. En
        effet, les mesures de confinement annoncées lundi 16 mars par Emmanuel
        Macron posent question, tant elles semblent modérées.
      </p>

      <p>
        Certains anticipaient des mesures drastiques de confinement total
        comparables à celles mises en place autour de la ville de Wuhan en
        Chine, Pourtant, en France, les transports publics fonctionnent, et
        chacun pourra se rendre normalement sur son lieu de travail s’il n’est
        pas en mesure de travailler de chez lui. Ces mesures timides suggèrent
        que le gouvernement pourrait faire,{" "}
        <a
          href="https://www.lefigaro.fr/sciences/la-france-mise-sur-l-immunite-de-groupe-pour-arreter-le-coronavirus-20200313"
          target="_blank"
          rel="noopener noreferrer"
        >
          sans le dire, le choix de la stratégie dite de l’”immunité de groupe”
        </a>
        .
      </p>

      <h2>Le tabou effrayant de l’immunité de groupe</h2>

      <p>
        L’immunité de groupe, c’est quoi ? Le principe est simple : si une
        fraction suffisante de la population est atteinte par le virus et
        devient immunisée, on empêcherait la propagation d’une nouvelle épidémie
        dans le futur. Mais laisser le virus contaminer une grande partie de la
        population dans un premier temps, plutôt que de l’endiguer, provoquera
        inéluctablement un grand nombre de décès. On peut même craindre
        plusieurs dizaines de milliers de morts, rien qu’en France.
      </p>

      <p>
        Afin de mieux comprendre cette stratégie de “l’immunité de groupe” - ou
        “immunité collective” - et la gravité potentielle de ses implications
        dans le contexte français, il nous faut comprendre la dynamique d’une
        épidémie. Nous avons donc mis au point une simulation, proche du modèle
        "SIR", et inspirée d'une modélisation réalisée par le{" "}
        <em>
          <a
            href="https://www.washingtonpost.com/graphics/2020/world/corona-simulator/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Washington Post
          </a>
        </em>{" "}
        (
          <a
            href="https://www.washingtonpost.com/people/harry-stevens/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Harry Stevens
          </a>
        ).
      </p>

      <p>
        Dans cette simulation, les individus sont répartis en trois groupes : en
        vert, les personnes saines et susceptibles d’être contaminées; en rouge,
        les personnes infectées par le virus ; en jaune, les personnes décédées
        ou “guéries” - on suppose que ces dernières sont immunisées et ne sont
        plus susceptibles de contracter et de propager à nouveau la maladie.
      </p>

      <p>
        Les interactions entre les personnes sont simulées par des chocs entre
        des billes. En cas de contact entre une personne saine (en vert) et une
        personne infectée (en rouge), la maladie est transmise. Au bout d’un
        certain temps, les personnes infectées “guérissent” et ne sont plus
        contaminantes. Elles ne peuvent plus non plus être contaminées : elles
        sont immunisées.
      </p>

      <p>
        Observons ce qu’il se passe si l’on introduit un nouveau virus parmi une
        population saine et non immunisée : c’est ce qu’il s’est produit avec
        l’introduction du SARS-CoV-2 :
      </p>

      <Waypoint onEnter={() => setStartFirstSimulation(true)} />
      <CovidHerdImmunityController start={startFirstSimulation} />

      <p>
        Au fur et à mesure des contacts successifs, la maladie se propage de
        manière exponentielle. C’est une véritable réaction en chaîne : plus il
        y a de personnes infectées, plus il y a de contaminations et plus le
        processus est rapide.
      </p>

      <p>
        Mais au bout d’un certain temps, la population immunisée a tellement
        augmenté que la plupart des porteurs du virus rencontrent des personnes
        déjà immunisées et l’épidémie ralentit. Après avoir atteint un pic, la
        proportion de malades diminue.
      </p>

      <h2>Des réactions en chaîne</h2>

      <p>
        Pour bloquer la propagation du virus, il peut être tentant de mettre en
        place des mesures de quarantaine autour d’une zone bien délimitée. C’est
        ce qu’a fait la Chine autour de la ville de Wuhan et de la région
        d’Hubei. On peut simuler cette situation en divisant nos billes dans
        deux compartiments séparés par une paroi. On injecte une personne
        infectée dans le compartiment de gauche. Pour tenir compte du fait qu’il
        est difficile de réaliser une quarantaine parfaitement étanche, on
        laisse un trou dans la paroi. Observons la dynamique :
      </p>

      <Waypoint onEnter={() => setStartSecondSimulation(true)} />
      <CovidHerdImmunityController
        start={startSecondSimulation}
        quarantine={0.92}
        prob={1}
        immunePopulation={0}
        confinedPopulation={0}
      />
      <p>
        L’infection progresse de manière exponentielle dans le compartiment sous
        quarantaine. Mais dans la plupart des cas, au bout d’un certain temps,
        une personne contaminée traverse la paroi. Dès lors, une nouvelle
        réaction en chaîne démarre dans le compartiment “préservé”. L’épidémie
        connaît un rebond, et au final, toute la population est affectée.
      </p>

      <h2>Le risque d’une épidémie rebondissante</h2>

      <p>
        Revenons maintenant à la réalité. Imaginons qu’il demeure, après une
        épidémie, quelque part dans le monde, un “réservoir” de malades. Par
        exemple, dans les campagnes chinoises, où l’infrastructure médicale est
        faible, et où de nombreux paysans contaminés ayant fui les villes
        pourraient se trouver. On l’a bien vu avec le coronavirus : il est
        difficile, avec les infrastructures de transport actuelles et
        l’imbrication des économies mondiales, de mettre au point des mesures de
        quarantaine réellement étanches. On peut donc craindre qu’une nouvelle
        propagation ait lieu au bout d’un certain temps. Et donc une nouvelle
        pandémie, avec toutes les mesures exceptionnelles et toutes les victimes
        qu’elle impliquerait. Le risque qui nous pend au nez est alors celui
        d’une épidémie rebondissante, qui s’en va et qui revient, et qui
        ralentit régulièrement les échanges et le commerce...
      </p>

      <p>
        C’est là que l’intérêt de l’immunité de groupe entre en jeu. Dès lors
        qu’une population est en grande partie immunisée parce qu’elle a
        rencontré le virus, le coronavirus deviendrait un virus “apprivoisé”.
      </p>

      <blockquote>
        Mais une telle stratégie, dont on peut admettre avec difficulté la
        froide logique - car elle implique de laisser mourir un certain nombre
        de personnes fragiles - devient irresponsable dès lors que la communauté
        qui la met en place n’a pas les moyens de prendre en charge les malades.
      </blockquote>

      <p>
        En effet, si la propagation de l’épidémie est trop rapide, il faut
        traiter beaucoup trop de malades en même temps - et potentiellement
        beaucoup plus que ne peuvent en absorber notre infrastructure et nos
        personnels de santé. Comme en Italie, où dans certains hôpitaux, faute
        de places en réanimation,{" "}
        <a
          href="https://www.la-croix.com/Monde/Europe/Coronavirus-lItalie-sous-equipee-face-crise-sanitaire-2020-03-08-1201082772?utm_term=Autofeed&utm_medium=Social&utm_source=Twitter#!#Echobox=1583685519"
          target="_blank"
          rel="noopener noreferrer"
        >
          on sacrifie des patients de seulement soixante ans
        </a>
        .
      </p>

      <figure>
        <img
          src={plotImg}
          alt="Graphique illustrant la stratégie de l'aplatissement de la courbure épidémique"
        />
        <figcaption>
          <strong>
            Stratégie de l'aplatissement de la courbure épidémique
          </strong>
          . Ralentir la propagation du virus peut permettre, dans le meilleur
          des cas, de contenir la quantité de malades à prendre en charge
          en-deça de la capcité du système de santé.
        </figcaption>
      </figure>

      <h2>Cruciales mesures de “distanciation sociale”</h2>

      <p>
        Pour ralentir la propagation, il faut donc mettre en place différentes
        mesures. Par exemple, les “<em>gestes barrières</em>”, qui diminuent les
        risques de transmission du virus en cas de contact. Ou encore des
        mesures de confinement, qui diminuent le nombre de contacts. Pour bien
        comprendre l’impact de telles mesures sur la propagation de l’épidémie,
        nous pouvons les intégrer dans notre simulation. Dans notre modèle, par
        exemple, les mesures de distanciation sociales reviennent à fixer une
        fraction des billes. Les mesures d’hygiène sont modélisées par une
        probabilité plus faible de contamination en cas de choc :
      </p>

      <Waypoint onEnter={() => setStartThirdSimulation(true)} />
      <CovidHerdImmunityController
        start={startThirdSimulation}
        quarantine={0}
        prob={0.5}
        immunePopulation={0}
        confinedPopulation={0.5}
      />

      <p>
        On observe très nettement la différence dans la vitesse de propagation.
        En ralentissant la propagation de l’épidémie, on peut s’assurer qu’à
        chaque instant, le nombre de malades ne dépasse pas excessivement la
        capacité du système de santé. Ce ralentissement permet donc de diminuer
        drastiquement le nombre de victimes.
      </p>

      <p>
        Mais dans chaque cas, c’est une partie significative de la population
        qui finit contaminée, puis immunisée : l’élément primordial, qui
        conditionne les conséquences sanitaires, c’est la vitesse du processus.
      </p>

      <p>
        Que se passe-t-il si, après cette première vague d’épidémie, un patient
        infecté est injecté parmi la population, désormais largement immunisée ?
        Observons :
      </p>

      <Waypoint onEnter={() => setStartFourthSimulation(true)} />
      <CovidHerdImmunityController
        start={startFourthSimulation}
        quarantine={0}
        prob={1}
        immunePopulation={0.8}
        confinedPopulation={0}
      />

      <p>
        Faute de personnes susceptibles d’être contaminées, l’épidémie ralentit
        très vite et s’arrête d’elle-même : c’est l’immunité de groupe. Que se
        passe-t-il si la fraction de personnes immunisées est plus faible ?
      </p>

      <Waypoint onEnter={() => setStartFifthSimulation(true)} />
      <CovidHerdImmunityController
        start={startFifthSimulation}
        quarantine={0}
        prob={1}
        immunePopulation={0.2}
        confinedPopulation={0}
      />

      <h2>La donnée-clé : le taux de reproduction de l’épidémie</h2>

      <p>
        Dans ce cas, manifestement, le seuil d’immunité de groupe n’a pas été
        atteint. Une nouvelle vague épidémique, susceptible de dépasser le
        système de santé, a lieu. Combien faut-il de personnes immunisées pour
        empêcher un deuxième épisode épidémique ?
      </p>

      <p>
        Pour le calculer, la donnée-clé est le taux de reproduction de
        l’épidémie, aussi appelé R0. Cette quantité, mesurée par diverses
        études, correspond grossièrement au nombre moyen de personnes
        contaminées par une personne infectée. Il dépend de la contagiosité
        intrinsèque du virus, mais aussi des comportements de la population, de
        sa densité, de sa culture, de son hygiène...
      </p>

      <p>
        Dans le cas du Coronavirus, sans précautions exceptionnelles (port de
        masque, lavage de main fréquent, pas de bise, confinement, etc.),{" "}
        <a
          href="https://www.thelancet.com/pdfs/journals/lancet/PIIS0140-6736(20)30260-9.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          ce coefficient semble être compris entre 2 et 3
        </a>
        . Supposons qu’il soit égal à 3 : cela signifierait qu’en moyenne,
        chaque individu infecté contamine 3 personnes. Pour que l’épidémie
        s’arrête, il faudrait qu’au moins deux de ces personnes, en moyenne,
        soient déjà immunisées. Ainsi, chaque porteur du virus, en moyenne,
        contaminerait réellement moins d’une personne - et la réaction en chaîne
        s’arrêterait.
      </p>

      <p>
        Cela signifie qu’il faudrait immuniser - et donc contaminer - les deux
        tiers de la population, soit, dans le cas de la France, plus de 40
        millions de personnes.
      </p>

      <h2>Des centaines de milliers de décès programmés ?</h2>

      <p>
        Quel serait, dès lors, le nombre de victimes ? Il est difficile de se
        prononcer à ce sujet, car les taux de létalité varient considérablement
        selon les catégories de population. Mais en faisant l’hypothèse d’une
        mortalité moyenne comprise entre 0,5 % et 1 % de la population, cela
        reviendrait à provoquer entre 200.000 et 400.000 décès.
      </p>

      <p>
        Dans le cadre de cette stratégie, il faudrait chercher à contaminer en
        priorité les personnes les moins vulnérables - les jeunes, notamment,
        dont le taux de mortalité est bien inférieur à la moyenne. Cela
        permettrait de contenir le nombre de victimes, qui pourrait tout de même
        atteindre plusieurs dizaines de milliers, sans compter celles et ceux
        qui souffriront de séquelles si des traitements plus efficaces ne voient
        pas rapidement le jour.
      </p>

      <p>
        Ces hypothèses impliquent également que le système de santé ne soit pas
        saturé, sans quoi la mortalité pourrait atteindre des taux bien plus
        élevés, comme en Italie,{" "}
        <a
          href="https://www.businessinsider.fr/us/coronavirus-death-rate-by-country-current-fatalities-compared-to-cases-2020-3"
          target="_blank"
          rel="noopener noreferrer"
        >
          où elle avoisinerait les 8 % parmi les cas confirmés
        </a>
        . Il n’est cependant pas aisé de contrôler la rapidité de l’épidémie de
        sorte à ce qu’elle soit juste assez élevée pour atteindre l’immunité de
        groupe, et juste assez basse pour permettre une prise en charge des
        malades.
      </p>

      <p>
        Au Royaume-Uni, le gouvernement de Boris Johnson est l’un des rares au
        monde à avoir assumé ouvertement le choix de l’immunité de groupe. Mais
        il n’a pas réfléchi à la gestion pratique de cette stratégie. Selon{" "}
        <a
          href="https://www.imperial.ac.uk/media/imperial-college/medicine/sph/ide/gida-fellowships/Imperial-College-COVID19-NPI-modelling-16-03-2020.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          une projection réalisée par l’équipe Covid-19 de l’Imperial College de
          Londres
        </a>
        , même des mesures drastiques de confinement et de fermetures d’écoles
        prolongées sur plusieurs mois ne seraient pas en mesure de contenir
        l’épidémie dans des proportions admissibles pour le système de santé
        local. Londres semble donc vouloir rétropédaler. Mais il est peut-être
        déjà trop tard. D’après le conseiller scientifique en chef du
        gouvernement du Royaume-Uni, Patrick Vallance,{" "}
        <a
          href="https://www.bloomberg.com/news/videos/2020-03-17/u-k-s-vallance-warns-20-000-dead-would-be-good-outcome-video"
          target="_blank"
          rel="noopener noreferrer"
        >
          un bilan de 20.000 morts constituerait “<em>un bon dénouement</em>”
          pour cette épidémie
        </a>
        .
      </p>

      <h2>L’immunité de groupe, une stratégie sans certitudes</h2>

      <p>
        Mais surtout, des interrogations cruciales demeurent : le virus ne
        pourrait-il pas muter, comme la grippe ? Les cas de réinfection sont-ils
        vraiment si rares que l’on peut considérer que toutes les personnes
        touchées sont réellement immunisées pour une durée suffisante ? D’après{" "}
        <a
          href="https://lemediatv.fr/emissions/2020/coronavirus-macron-a-t-il-choisi-de-laisser-mourir-les-francais-st8-Yg2BSDWB7dcMPMW6sA/eyJwYXlsb2FkIjp7fX0.XnOv1w.tFvg4oTCK5tsuLpT-eTPfiogLa0/"
          target="_blank"
          rel="noopener noreferrer"
        >
          le chercheur en biologie computationnelle François Balloux
        </a>
        , “
        <em>
          la comparaison avec les autres Coronavirus suggère que l’[immunité]
          pourrait être relativement courte (de quelques mois)
        </em>
        ”. Si tel était le cas, d’importantes vagues d’épidémies pourraient
        resurgir. Sur ces points, nous n’avons pas de certitudes pour le moment.
        D’après la porte-parole de l’OMS Margaret Harris, “
        <em>
          <a
            href="https://www.ft.com/content/64d54c97-6c8d-31e3-848c-be0e93cadc52"
            target="_blank"
            rel="noopener noreferrer"
          >
            ce virus n’a pas circulé assez longtemps dans nos populations pour
            que nous sachions comment nous y réagissons en termes immunologiques
          </a>
        </em>
        ”.
      </p>

      <p>
        Un choix si risqué donc, qu’il apparaît davantage subi que délibéré. Le
        manque d’anticipation du gouvernement français, de l’aveu même de son
        ex-ministre de la Santé, la pénurie de masques, ou peut-être même la
        crainte d’impacter trop durablement l’économie par des mesures
        véritablement drastiques et prolongées pourraient expliquer ce choix
        forcé. Peut-on dire avec certitude que le gouvernement a fait le pari de
        la stratégie de l’immunité de groupe ? Selon Samuel Alizon, directeur de
        recherche au CNRS et spécialiste en écologie évolutive des maladies
        infectieuses, si les mesures de confinement sont prolongées dans 15
        jours, cela suggérerait qu’il espère encore endiguer l’épidémie. Dans le
        cas contraire, cela pourrait signifier que le gouvernement a bien
        renoncé à stopper sa propagation et privilégie l’immunité de masse.
      </p>

      <h2>Ce qu’il aurait fallu faire… mais c’est trop tard</h2>

      <p>
        Si tel était le cas, en l’absence de traitement réellement efficace et
        en l’attente d’un éventuel vaccin, il faudrait donc s’attendre à
        peut-être plusieurs dizaines de milliers de morts rien qu’en France. Le
        pari de l’immunité de groupe est risqué et désespéré. La solution
        parfaite eût été une coopération immédiate de tous les pays afin de
        mettre fin à l’infection, avec des mesures drastiques, bornées dans le
        temps, jusqu’à la disparition du virus. Peut-être aurait-elle été
        possible, si la Chine n’avait pas perdu plusieurs semaines en entravant
        le travail des scientifiques et en ne prenant pas les mesures qui
        s’imposaient alors.
      </p>

      <p>
        Chaque population doit-elle désormais espérer, égoïstement, atteindre
        une immunité suffisante pour survivre à une prochaine épidémie, quitte à
        propager l’infection à des pays moins développés et moins préparés ?
        Mesure-t-on le sacrifice que cela représente, et, étant données les
        incertitudes qui planent sur ce choix, la gravité des répercussions s’il
        s’avérait être le mauvais ? Ce choix n’est-il pas, pour reprendre la
        célèbre formule, le pire à l’exception de tous les autres ? La réponse
        ne va pas tarder à se faire connaître.
      </p>

      <Waypoint onEnter={() => setStartCustomSimulation(true)} />
      <CovidHerdImmunityController start={startCustomSimulation} enableInputs />
    </>
  );
}

export default CovidHerdImmunity;
